import type { LayerDef } from "../types";
import { PRICE_RAMP, rampExpression, rampLegend } from "../ramps";
import { LEVELS, type PriceLevel } from "../levels";

export const PRICE_YEARS = [2021, 2022, 2023, 2024, 2025];
export const DEFAULT_YEAR = 2025;

/** Attribute holding the median EUR/m2 for a given year. */
export const priceField = (year: number) => `p${year}`;
/** Attribute holding the transaction count for a given year. */
export const countField = (year: number) => `n${year}`;

/**
 * The selected year's median, falling back to the zone's most recent year with
 * data. Without the fallback a zone that was merely quiet in one year would
 * blink out of the map, which reads as "no such place" rather than "too few
 * sales to report".
 */
const priceValue = (year: number) => [
  "coalesce",
  ["get", priceField(year)],
  ["get", "p_latest"],
];

export function priceColorExpression(year: number): unknown[] {
  const value = priceValue(year);
  return [
    "case",
    ["==", value, null],
    "rgba(0,0,0,0)",
    rampExpression(PRICE_RAMP, value),
  ];
}

/** "9.9k" - compact enough to sit inside a small city without crowding. */
export function priceLabelExpression(year: number): unknown[] {
  const value = priceValue(year);
  return [
    "case",
    ["==", value, null],
    "",
    ["concat", ["to-string", ["/", ["round", ["/", value, 100]], 10]], "k"],
  ];
}

const HAS_DATA = ["has", "p_latest"];

const LEGEND = rampLegend(PRICE_RAMP);

/**
 * One fill, one outline and one label layer per level. All three levels are
 * loaded up front and switched by visibility, so changing level is a repaint
 * rather than a fetch.
 */
function layersForLevel(level: PriceLevel) {
  const def = LEVELS.find((l) => l.id === level)!;
  const isIris = level === "iris";
  return [
    {
      // The click target, and nothing else: fully transparent, and left visible
      // whatever the price layer is toggled to. Selection is a property of the
      // map, not of one layer — with this drawn by the price fill instead, a
      // user comparing population or noise with prices switched off found that
      // clicking the map silently did nothing at all.
      //
      // It also carries no HAS_DATA filter, so a zone whose sales are
      // suppressed can still be opened. It has no median to show, but it still
      // has people, transit and air worth reading, and the panel already says
      // so in place of a price.
      id: def.hitId,
      type: "fill" as const,
      source: def.source,
      "source-layer": def.sourceLayer,
      paint: { "fill-color": "#000000", "fill-opacity": 0 },
    },
    {
      id: def.fillId,
      type: "fill" as const,
      source: def.source,
      "source-layer": def.sourceLayer,
      filter: HAS_DATA as never,
      paint: {
        "fill-color": priceColorExpression(DEFAULT_YEAR) as never,
        "fill-opacity": 0.62,
      },
    },
    {
      id: def.lineId,
      type: "line" as const,
      source: def.source,
      "source-layer": def.sourceLayer,
      paint: {
        "line-color": "#ffffff",
        // Bigger units get a firmer edge, so a department reads as one shape
        // rather than a patchwork.
        "line-width": level === "departement" ? 1.6 : isIris ? 0.5 : 0.9,
        "line-opacity": isIris ? 0.55 : 0.85,
      },
    },
    {
      id: def.labelId,
      type: "symbol" as const,
      source: def.source,
      "source-layer": def.sourceLayer,
      filter: HAS_DATA as never,
      layout: {
        "text-field": priceLabelExpression(DEFAULT_YEAR) as never,
        "text-font": ["Noto Sans Medium"] as never,
        "text-size": isIris ? 10 : 12,
        // Labels are a read-out, not decoration: if two collide, dropping one
        // beats overlapping numbers nobody can read.
        "text-allow-overlap": false,
        "text-padding": 4,
      },
      paint: {
        "text-color": "#14181f",
        "text-halo-color": "#ffffff",
        "text-halo-width": 1.6,
      },
    },
    {
      // A layer's source is fixed once added, so each level carries its own
      // selection outline rather than one shared layer being re-pointed.
      id: def.selectedId,
      type: "line" as const,
      source: def.source,
      "source-layer": def.sourceLayer,
      filter: ["==", ["get", "code"], " "] as never,
      paint: { "line-color": "#14181f", "line-width": 2.5 },
    },
  ];
}

export const priceLayers: LayerDef = {
  id: "prices",
  label: "Flat prices",
  group: "prices",
  description:
    "Median price per square metre, by department, city or neighbourhood.",
  attribution:
    '<a href="https://www.data.gouv.fr/datasets/demandes-de-valeurs-foncieres-geolocalisees">DVF - DGFiP/Etalab</a>',
  defaultVisible: true,
  usesYear: true,
  legend: LEGEND,
  sources: {
    prices_departement: {
      type: "vector",
      url: "pmtiles:///tiles/prices_departement.pmtiles",
    },
    prices_commune: {
      type: "vector",
      url: "pmtiles:///tiles/prices_commune.pmtiles",
    },
    prices_iris: {
      type: "vector",
      url: "pmtiles:///tiles/prices_iris.pmtiles",
    },
  },
  layers: [
    ...layersForLevel("departement"),
    ...layersForLevel("commune"),
    ...layersForLevel("iris"),
  ],
};

/** Fill layers whose colour depends on the selected year. */
export const PRICE_FILL_LAYERS = LEVELS.map((l) => l.fillId);
/** Label layers whose text depends on the selected year. */
export const PRICE_LABEL_LAYERS = LEVELS.map((l) => l.labelId);
